/**
 * Play the video for based on source to the calling component. It has a Text component for description for video incase of browser doesn't support video player.
 *
 * @module views/components/Video
 * @memberof -Common
 */
import './Video.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getVideoUrl, VIDEO_FORMATS } from '@ulta/core/utils/media/media';

import Text from './../Text/Text';

/**
  * Represents a Video component
  *
  * @method
  * @param {VideoProps} props - React properties passed from composition
  * @returns Video
  */
export const Video = function( props ){
  const videoStyles = {
    ...( props.imageWidth && { width: `${ props.imageWidth }px` } )
  };

  const videoUrl = getVideoUrl( { videoProfiles: props.videoProfiles, format: VIDEO_FORMATS.VP9720P, defaultUrl: props.videoUrl } );

  return (
    <div className={ classNames( 'Video', getVideoDimensionClasses( props?.metaData ) ) }
      aria-label={ props.ariaLabel }
      style={ videoStyles }
    >
      { /* eslint-disable-next-line jsx-a11y/media-has-caption */ }
      <video
        controls
        { ...spreadProps( {
          preload: props.preloadNone
        } ) }
        poster={ props.imageUrl }
        ref={ props.videoRef }
        onPlay={ props.clickHandler }
        onBlur={ props.blurHandler }
        onPause={ props.pauseHandler }
        src={ videoUrl }
      >
        <source src={ videoUrl }
          type={ props.metaData?.format === 'mpeg4' ? 'video/mp4' : 'video/webm' }
        />
        {
          props.videoProfiles?.length && props.videoProfiles.map( ( profile, i ) => (
            <source key={ i }
              src={ profile.url }
              type={ ( profile.metaData?.format === 'mpeg4' ) ? 'video/mp4' : 'video/webm' }
            />
          )
          )
        }
        { props.isCaptionsEnabled &&
          <track kind='captions'
            src={ props.isCaptionsEnabled && props.closedCaptionUrl }
            default
          >
          </track>
        }
        <Text>
          { props.altText }
        </Text>
      </video>
    </div>
  );
};

/**
   * @method getVideoDimensionClasses
   * @summary Function return a className value based on videoDimisionWe should return default to horizontal, if the video is missing meta data.
   * @param {object}  - videoMetaData object
   * @returns {string}
   */
export const getVideoDimensionClasses = ( videoMetaData ) => {
  const videoHasDimensions =  !!( videoMetaData?.height && videoMetaData?.width );
  return videoHasDimensions && videoMetaData.width < videoMetaData.height ? 'Video--VideoVertical' : 'Video--VideoHorizontal';
};

/**
  * Property type definitions
  * @typedef VideoProps
  * @type {object}
  * @property {string} imageUrl - This is the videoAsset (location/description) for video.
  * @property {string} name - This sets the name
  * @property {string} altText - Sets the alt text
  * @property {boolean} isCaptionsEnabled - Sets the caption flag
  * @property {string} closedCaptionUrl - Sets the caption src url
  * @property {videoUrl} videoUrl - Sets the src of video url
  * @property {videoProfiles} videoProfiles - Sets the video profile list
  * @property {ariaLabel} ariaLabel - Sets the aria-label value
  * @property {number} imageWidth - Sets the poster width
  * @property {function} clickHandler - Handles the video player icon click event
  * @property {function} blurHandler - Handles the video blur event
  * @property {function} pauseHandler - Handles the video pause event
  */
export const propTypes =  {
  imageUrl: PropTypes.string,
  name: PropTypes.string,
  altText: PropTypes.string,
  isCaptionsEnabled: PropTypes.bool,
  closedCaptionUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  videoProfiles: PropTypes.array,
  ariaLabel: PropTypes.string,
  imageWidth: PropTypes.number,
  clickHandler: PropTypes.func,
  blurHandler: PropTypes.func,
  pauseHandler: PropTypes.func
};

Video.propTypes = propTypes;

export default Video;
